import { Box, Flex, Text, useDisclosure, VisuallyHidden } from '@chakra-ui/react';
import { BodyWrapper } from 'components/wrapper/bodyWrapper';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { normalize } from 'pages/api/sitemap';
import React, { FunctionComponent, useEffect, useState } from 'react';
import CountUp from 'react-countup';
import { withTranslations } from 'store/translations';
import {
  getDecimalPlaces,
  getDisplayFormat,
  getLocaleNumberFormat,
  hasAnimatableNumbers,
  parseLocalizedNumber,
  removeSuffixedStrings,
  separateNumbers,
} from 'utils/factNumberFormatting';
import { imageFormatter } from 'utils/imageFormatter';
import { hrefLink } from 'utils/normalization/hrefLink';

import { htmlResolver } from '../../helpers/htmlResolver';
import { LinkType } from '../../lib/enums/LinkType';
import { ContentPage } from '../../models';
import { Fact as FactModel } from '../../models/content-types/fact';
import { Link } from '../base/Link';
import { ContactFormModal } from './ContactFormModal';
//import { mode } from '@chakra-ui/theme-tools';

export enum FactParent {
  FACTBOX = 'factbox',
  TEXT_MODULE = 'text_module',
}

interface FactProps {
  model: FactModel;
  backgroundColor?: string;
  accentColor?: string;
  bodyColor?: string;
  childOf?: FactParent;
}

const MotionText = motion(Text);

const parentStyles = {
  factbox: {
    gap: 'xxs',
  },
  text_module: {
    gap: 'xs',
    p: { base: 'xs', lg: 's' },
  },
};

export const Fact: FunctionComponent<FactProps> = ({
  model,
  backgroundColor,
  accentColor,
  bodyColor,
  childOf = FactParent.FACTBOX,
}) => {
  const router = useRouter();
  const translations = withTranslations();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [cacheKey, setCacheKey] = useState(0);

  useEffect(() => {
    fetchItems();
  }, []);
  useEffect(() => {
    translations.isReady && fetchItems();
  }, [router.locale, translations.locale]);

  // Force cache clear when locale changes
  useEffect(() => {
    setCacheKey((prev) => prev + 1);
  }, [router.locale]);

  const factText = model.elements.factAsText.value;
  const currentLocale = router.locale || process.env.NEXT_PUBLIC_DEFAULT_LOCALE!;
  const factTextArray = separateNumbers(factText);
  const hasNumbers = hasAnimatableNumbers(factText);

  const description = htmlResolver(model.elements.description);
  const icon = model.elements.factAsIcon.value;
  const iconalt = model.elements.alternativeText.value;
  const compressImage = model.elements.compressFactImage.value ? model.elements.compressFactImage.value : 75;
  const slug = router.query.slug || router.query.mainSubpage || router.query.subcategory;
  const url = hrefLink(model, (slug as unknown as string)?.split('/'), false, router.locale, translations);
  const fileFrontify = JSON.parse(model.elements.ctaSnippetFrontifyFile.value);
  const ctaSnippetCtaText = model.elements.ctaSnippetCtaText.value;
  const formModalCtaButtonText = model.elements.formModalCtaButtonText.value;
  const ctaSnippetKontentAi = model.elements.ctaSnippetKontentAiUrl.value[0];
  const path = router.asPath.slice(0, router.asPath.lastIndexOf('/'));
  const image = imageFormatter(icon, iconalt, false, compressImage);
  const [ctaSnippetKontentAiUrl, setCtaSnippetKontentAiUrl] = useState<string>();

  const setUrl = async () => {
    if (ctaSnippetKontentAi) {
      const item = (await fetch(`/api/sitemap?codename=${ctaSnippetKontentAi}&locale=${router.locale}`)
        .then((res) => res.json())
        .catch(() => null)) as ContentPage | null;
      if (translations.isReady) {
        return item ? Object.keys(normalize(item, router.locale, undefined, true, translations))?.[0] : '';
      }
    }
  };
  const fetchItems = async () => {
    const url = !router.asPath.includes('/editors-only')
      ? await setUrl()
      : ctaSnippetKontentAi != undefined
      ? `${path}/${ctaSnippetKontentAi}`
      : null;
    url && setCtaSnippetKontentAiUrl(url);
  };
  return (
    <Flex
      key={`fact-${currentLocale}-${factText}-${cacheKey}`}
      bg={backgroundColor}
      flexDir='column'
      sx={parentStyles[childOf]}
    >
      <VisuallyHidden>{`${translations.aria('fact')}: ${factText}`}</VisuallyHidden>
      {factText ? (
        <Box
          aria-hidden='true'
          color={accentColor}
          whiteSpace={'pre-wrap'}
          overflowWrap='break-word'
          overflow={'hidden'}
          flexWrap='wrap'
          display='inline-flex'
          textStyle={{ base: 'mobile.h1', md: 'desktop.h3' }}
        >
          {!hasNumbers ? (
            <Text w='100%' sx={{ hyphens: 'auto' }}>
              {factText}
            </Text>
          ) : (
            factTextArray.map((item, index) => {
              if (!item) return null;

              // Skip items that were already processed as ordinal suffixes
              if (index > 0) {
                const prevItem = factTextArray[index - 1];
                const prevNumericValue = parseLocalizedNumber(prevItem);
                if (!isNaN(prevNumericValue) && prevNumericValue !== 0) {
                  const currentItemIsOrdinal = /^(st|nd|rd|th)\b/i.test(item.trim());
                  if (currentItemIsOrdinal) {
                    return null; // Skip this item as it was already processed with the previous number
                  }
                }
              }

              // Try to parse this item as a number
              const numericValue = parseLocalizedNumber(item);

              if (!isNaN(numericValue) && numericValue !== 0) {
                // Check if the next item is an ordinal suffix
                const nextItem = factTextArray[index + 1];
                const isOrdinal = nextItem && /^(st|nd|rd|th)\b/i.test(nextItem.trim());

                // If it's an ordinal number, don't animate it
                if (isOrdinal) {
                  // Extract just the ordinal suffix (st, nd, rd, th)
                  const ordinalMatch = nextItem.match(/^(st|nd|rd|th)/i);
                  const ordinalSuffix = ordinalMatch ? ordinalMatch[1] : '';
                  const remainingText = nextItem.substring(ordinalSuffix.length);

                  return (
                    <React.Fragment key={`${index}-${currentLocale}-ordinal`}>
                      <Text sx={{ hyphens: 'auto' }} display='inline'>
                        {item}
                        {ordinalSuffix}
                      </Text>
                      {remainingText && (
                        <Text sx={{ hyphens: 'auto' }} display='inline'>
                          {remainingText}
                        </Text>
                      )}
                    </React.Fragment>
                  );
                }

                // Get format from the original text - no locale assumptions
                const decimals = getDecimalPlaces(item);
                const displayFormat = getDisplayFormat(item);

                // Animate the number while preserving the original formatting style
                return (
                  <CountUp
                    key={`${index}-${currentLocale}-${item}-${numericValue}`}
                    enableScrollSpy={true}
                    scrollSpyOnce={true}
                    start={0}
                    end={numericValue}
                    duration={3}
                    useEasing={true}
                    redraw={true}
                    separator={displayFormat.thousandSeparator}
                    decimal={displayFormat.decimalSeparator}
                    decimals={decimals}
                  >
                    {({ countUpRef }) => <MotionText ref={countUpRef} />}
                  </CountUp>
                );
              }

              // For non-numeric items, just display as text
              return (
                <Text key={`${index}-${currentLocale}-text`} sx={{ hyphens: 'auto' }}>
                  {item}
                </Text>
              );
            })
          )}
        </Box>
      ) : (
        icon && <Image width={64} height={64} src={image.src} alt={image.alt} quality={image.quality} />
      )}
      {model.elements.headline.value !== '' && (
        <Text color={bodyColor} textStyle={{ base: 'desktop.body.medium.s', lg: 'desktop.body.book.l' }}>
          {model.elements.headline.value}
        </Text>
      )}
      <Box color={bodyColor} textStyle={{ base: 'mobile.body.s', lg: 'desktop.body.l' }}>
        {description?.map((text, index) => (
          <BodyWrapper key={index} sx={index ? { pt: 'xxs' } : {}} tag={text.tag} body={text.body} />
        ))}
      </Box>
      {model.elements.ctaSnippetUrl.value && url ? (
        <Link
          href={url}
          iconName='chevronRightForLink'
          colorScheme={accentColor}
          type={LinkType.SecondaryButton}
          isExternal={model.elements.ctaSnippetOpenIn.value[0]?.codename === 'new_tab'}
          locale={router.locale}
        >
          {ctaSnippetCtaText}
        </Link>
      ) : ctaSnippetKontentAiUrl ? (
        <Link
          href={ctaSnippetKontentAiUrl}
          iconName='chevronRightForLink'
          colorScheme={accentColor}
          type={LinkType.SecondaryButton}
          isExternal={model.elements.ctaSnippetOpenIn.value[0]?.codename === 'new_tab'}
          locale={router.locale}
        >
          {ctaSnippetCtaText}
        </Link>
      ) : fileFrontify ? (
        <Link
          href={fileFrontify.downloadUrl}
          iconName='chevronRightForLink'
          colorScheme={accentColor}
          type={LinkType.SecondaryButton}
        >
          {ctaSnippetCtaText}
        </Link>
      ) : null}
      {model.elements.formModalCtaFormModal.linkedItems[0] && formModalCtaButtonText && (
        <>
          <Link
            href='#'
            iconName='chevronRightForLink'
            colorScheme={accentColor}
            type={LinkType.SecondaryButton}
            locale={router.locale}
            onClick={(e) => {
              e?.preventDefault();
              onOpen();
            }}
          >
            {formModalCtaButtonText}
          </Link>
          <ContactFormModal
            id={`${model.system.id}${model.elements.formModalCtaFormModal.linkedItems[0].system.id}`}
            model={model.elements.formModalCtaFormModal.linkedItems[0]}
            isOpen={isOpen}
            onClose={onClose}
          />
        </>
      )}
    </Flex>
  );
};
